import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const StockAdjustmentModal = ({ product, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    transaction_type: 'stock_in',
    quantityChange: '',
    location: 'Main Warehouse',
    assignedTechnician: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const transactionTypes = [
    { value: 'stock_in', label: 'Stock In (Add)' },
    { value: 'stock_out', label: 'Stock Out (Remove)' },
    { value: 'adjustment', label: 'Adjustment' },
    { value: 'transfer', label: 'Transfer' },
    { value: 'reorder', label: 'Reorder' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.quantityChange || formData?.quantityChange === '0') {
      newErrors.quantityChange = 'Quantity change is required and must not be zero';
    }

    const quantityValue = parseInt(formData?.quantityChange);
    if (isNaN(quantityValue)) {
      newErrors.quantityChange = 'Quantity must be a valid number';
    }

    // For stock_out, ensure we don't go negative
    if (formData?.transaction_type === 'stock_out' && quantityValue > 0) {
      const newQuantity = product?.quantity - quantityValue;
      if (newQuantity < 0) {
        newErrors.quantityChange = `Cannot remove ${quantityValue} items. Only ${product?.quantity} available.`;
      }
    }

    if (!formData?.location?.trim()) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      let quantityChange = parseInt(formData?.quantityChange);
      
      // For stock_out, make the quantity negative
      if (formData?.transaction_type === 'stock_out') {
        quantityChange = -Math.abs(quantityChange);
      } else {
        quantityChange = Math.abs(quantityChange);
      }

      await onSave({
        ...formData,
        quantityChange
      });
    } catch (err) {
      console.error('Error submitting adjustment:', err);
      setErrors({ submit: err?.message || 'Failed to save adjustment' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl border border-border shadow-elevation-3 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-heading font-semibold text-foreground">
              Stock Adjustment
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              {product?.name} ({product?.sku})
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} color="var(--color-foreground)" />
          </button>
        </div>

        {/* Current Stock Info */}
        <div className="p-6 bg-muted/30 border-b border-border">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Current Stock</p>
              <p className="text-2xl font-semibold text-foreground">{product?.quantity}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Reorder Level</p>
              <p className="text-2xl font-semibold text-foreground">{product?.reorder_level}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Status</p>
              <p className="text-lg font-medium text-foreground">{product?.status}</p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <Select
            label="Transaction Type"
            required
            value={formData?.transaction_type}
            onChange={(e) => handleInputChange('transaction_type', e?.target?.value)}
            options={transactionTypes}
          />

          <Input
            type="number"
            label="Quantity"
            required
            placeholder="Enter quantity"
            value={formData?.quantityChange}
            onChange={(e) => handleInputChange('quantityChange', e?.target?.value)}
            error={errors?.quantityChange}
            description={formData?.transaction_type === 'stock_out' ? 'Enter positive number to remove from stock' : 'Enter quantity to add'}
          />

          <Input
            type="text"
            label="Location"
            required
            placeholder="e.g., Main Warehouse, Truck #5"
            value={formData?.location}
            onChange={(e) => handleInputChange('location', e?.target?.value)}
            error={errors?.location}
          />

          <Input
            type="text"
            label="Assigned Technician"
            placeholder="Technician name (optional)"
            value={formData?.assignedTechnician}
            onChange={(e) => handleInputChange('assignedTechnician', e?.target?.value)}
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Notes
            </label>
            <textarea
              placeholder="Add notes about this adjustment..."
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
              rows={3}
              className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          {errors?.submit && (
            <div className="p-3 bg-destructive/10 border border-destructive rounded-lg">
              <p className="text-sm text-destructive">{errors?.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={loading}
              disabled={loading}
            >
              Save Adjustment
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockAdjustmentModal;